import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import {
  Building2, LayoutDashboard, Users, FileText, Settings, LogOut,
  BarChart3, Home, UserCircle, Menu, ChevronRight, Briefcase,
  MapPin, DollarSign, Globe, Image, MessageSquare, Bell, Trophy, Target,
  PhoneCall, UserPlus, Lightbulb, Map, Receipt, Mail, Layers, Calendar
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, builder, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation items grouped by category based on role
  const getNavCategories = () => {
    const role = user?.role;
    
    if (role === 'super_admin') {
      return [
        {
          category: 'OVERVIEW',
          items: [
            { icon: LayoutDashboard, label: 'Dashboard', href: '/admin' },
          ]
        },
        {
          category: 'MANAGEMENT',
          items: [
            { icon: Building2, label: 'Builders', href: '/admin/builders' },
            { icon: Users, label: 'Users', href: '/admin/users' },
          ]
        },
        {
          category: 'SETTINGS',
          items: [
            { icon: Mail, label: 'Email Providers', href: '/admin/email-settings' },
            { icon: Settings, label: 'Settings', href: '/admin/settings' },
          ]
        },
      ];
    }

    if (role === 'builder_admin') {
      return [
        {
          category: 'OVERVIEW',
          items: [
            { icon: LayoutDashboard, label: 'Dashboard', href: '/builder' },
            { icon: BarChart3, label: 'Analytics', href: '/builder/analytics' },
          ]
        },
        {
          category: 'SALES',
          items: [
            { icon: PhoneCall, label: 'CRM', href: '/builder/crm' },
            { icon: Calendar, label: 'My Calendar', href: '/builder/calendar' },
            { icon: FileText, label: 'Client Tagging', href: '/builder/tagging' },
            { icon: MapPin, label: 'Site Visits', href: '/builder/site-visits' },
          ]
        },
        {
          category: 'PROJECTS',
          items: [
            { icon: Home, label: 'Projects', href: '/builder/projects' },
            { icon: Layers, label: 'Inventory', href: '/builder/inventory' },
            { icon: Map, label: 'Floor Plans', href: '/builder/floor-plans' },
          ]
        },
        {
          category: 'FINANCIALS',
          items: [
            { icon: DollarSign, label: 'Commissions', href: '/builder/commissions' },
            { icon: Receipt, label: 'Invoices', href: '/builder/invoices' },
            { icon: Mail, label: 'Demand Letters', href: '/builder/demand-letters' },
          ]
        },
        {
          category: 'PARTNERS',
          items: [
            { icon: Users, label: 'Channel Partners', href: '/builder/partners' },
            { icon: Trophy, label: 'Leaderboard', href: '/builder/leaderboard' },
            { icon: Target, label: 'Goals', href: '/builder/goals' },
            { icon: BarChart3, label: 'Performance', href: '/builder/performance' },
          ]
        },
        {
          category: 'MARKETING',
          items: [
            { icon: Globe, label: 'Microsites', href: '/builder/microsites' },
            { icon: Image, label: 'Collateral Hub', href: '/builder/collaterals' },
            { icon: Lightbulb, label: 'Sales Enablement', href: '/builder/sales-enablement' },
          ]
        },
        {
          category: 'CUSTOMERS',
          items: [
            { icon: Home, label: 'Buyers', href: '/builder/buyers' },
          ]
        },
        {
          category: 'SETTINGS',
          items: [
            { icon: Users, label: 'Team', href: '/builder/team' },
            { icon: UserPlus, label: 'Sub-Users', href: '/builder/sub-users' },
            { icon: Settings, label: 'Branding', href: '/builder/branding' },
            { icon: Globe, label: 'Domain Settings', href: '/builder/domain-settings' },
            { icon: Mail, label: 'Email Settings', href: '/builder/email-settings' },
          ]
        },
      ];
    }

    if (role === 'sales_team') {
      return [
        {
          category: 'OVERVIEW',
          items: [
            { icon: LayoutDashboard, label: 'Dashboard', href: '/sales' },
            { icon: BarChart3, label: 'Performance', href: '/sales/performance' },
          ]
        },
        {
          category: 'SALES',
          items: [
            { icon: PhoneCall, label: 'CRM', href: '/sales/crm' },
            { icon: Calendar, label: 'My Calendar', href: '/sales/calendar' },
            { icon: FileText, label: 'Client Tagging', href: '/sales/tagging' },
            { icon: MapPin, label: 'Site Visits', href: '/sales/site-visits' },
            { icon: Layers, label: 'Inventory', href: '/sales/inventory' },
          ]
        },
        {
          category: 'PARTNERS',
          items: [
            { icon: Users, label: 'Partners', href: '/sales/partners' },
            { icon: Trophy, label: 'Leaderboard', href: '/sales/leaderboard' },
            { icon: Target, label: 'Goals', href: '/sales/goals' },
          ]
        },
      ];
    }

    if (role === 'marketing_team') {
      return [
        {
          category: 'OVERVIEW',
          items: [
            { icon: LayoutDashboard, label: 'Dashboard', href: '/marketing' },
          ]
        },
        {
          category: 'MARKETING',
          items: [
            { icon: Image, label: 'Collaterals', href: '/marketing/collaterals' },
            { icon: Globe, label: 'Microsites', href: '/marketing/microsites' },
            { icon: Lightbulb, label: 'Sales Enablement', href: '/marketing/sales-enablement' },
            { icon: Settings, label: 'Templates', href: '/marketing/templates' },
          ]
        },
      ];
    }

    if (role === 'partner' || role === 'sub_user') {
      return [
        {
          category: 'OVERVIEW',
          items: [
            { icon: LayoutDashboard, label: 'Dashboard', href: '/partner' },
            { icon: BarChart3, label: 'Analytics', href: '/partner/analytics' },
          ]
        },
        {
          category: 'SALES',
          items: [
            { icon: PhoneCall, label: 'CRM', href: '/partner/crm' },
            { icon: Calendar, label: 'My Calendar', href: '/partner/calendar' },
            { icon: FileText, label: 'Client Tagging', href: '/partner/tagging' },
            { icon: MapPin, label: 'Site Visits', href: '/partner/site-visits' },
          ]
        },
        {
          category: 'PROJECTS',
          items: [
            { icon: Home, label: 'Projects', href: '/partner/projects' },
            { icon: Layers, label: 'Inventory', href: '/partner/inventory' },
            { icon: Map, label: 'Floor Plans', href: '/partner/floor-plans' },
          ]
        },
        {
          category: 'FINANCIALS',
          items: [
            { icon: DollarSign, label: 'Commissions', href: '/partner/commissions' },
            { icon: Receipt, label: 'My Invoices', href: '/partner/invoices' },
          ]
        },
        {
          category: 'MARKETING',
          items: [
            { icon: Globe, label: 'My Microsites', href: '/partner/microsites' },
            ...(role === 'partner' ? [
              { icon: Globe, label: 'Domain Management', href: '/partner/microsites/domains' }
            ] : []),
          ]
        },
        {
          category: 'PERFORMANCE',
          items: [
            { icon: Trophy, label: 'Leaderboard', href: '/partner/leaderboard' },
            ...(role === 'partner' ? [
              { icon: BarChart3, label: 'Team Performance', href: '/partner/performance' }
            ] : []),
          ]
        },
        ...(role === 'partner' ? [{
          category: 'TEAM',
          items: [
            { icon: Users, label: 'My Team', href: '/partner/team' },
            { icon: UserPlus, label: 'Sub-Users', href: '/partner/sub-users' },
          ]
        }] : []),
        {
          category: 'SETTINGS',
          items: [
            { icon: Settings, label: 'Settings', href: '/partner/settings' },
          ]
        },
      ];
    }

    return [];
  };

  const navCategories = getNavCategories();
  
  // Flatten nav items for mobile navigation
  const flatNavItems = navCategories.flatMap(cat => cat.items);
  
  // Get role display name
  const getRoleDisplay = (role) => {
    const names = {
      'super_admin': 'Super Admin',
      'builder_admin': 'Builder Admin',
      'sales_team': 'Sales Team',
      'marketing_team': 'Marketing Team',
      'partner': 'Channel Partner',
      'sub_user': 'Team Member',
    };
    return names[role] || role;
  };

  // Get sub-user parent info
  const getSubUserParentInfo = () => {
    if (user?.role !== 'sub_user') return null;
    
    if (user?.partner_id) {
      return { type: 'Channel Partner', name: user?.parent_name || 'Channel Partner' };
    }
    if (user?.builder_id && !user?.partner_id) {
      return { type: 'Builder', name: builder?.name || 'Builder' };
    }
    return null;
  };

  const subUserParent = getSubUserParentInfo();

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
        <Building2 className="h-8 w-8 text-amber-600" />
        <div>
          <span className="text-lg font-bold">effyRealty</span>
          {builder && (
            <p className="text-xs text-muted-foreground truncate max-w-[140px]">
              {builder.name}
            </p>
          )}
        </div>
      </div>

      {/* Nav Items with Categories */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navCategories.map((category, catIndex) => (
          <div key={category.category} className={catIndex > 0 ? 'mt-4' : ''}>
            <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              {category.category}
            </p>
            <div className="space-y-1">
              {category.items.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User Info */}
      <div className="border-t border-border p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-amber-100 text-amber-700">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{getRoleDisplay(user?.role)}</p>
            {subUserParent && (
              <p className="text-xs text-amber-600 truncate">
                under {subUserParent.name}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 md:fixed md:inset-y-0 bg-card border-r border-border">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden glass-header fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="btn-mobile-menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SidebarContent />
            </SheetContent>
          </Sheet>
          <Building2 className="h-6 w-6 text-amber-600" />
          <span className="font-bold">effyRealty</span>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon">
            <Bell className="h-5 w-5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-amber-100 text-amber-700 text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email || user?.phone}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen overflow-x-hidden">
        {/* Desktop Header */}
        <header className="hidden md:flex glass-header sticky top-0 z-30 items-center justify-between px-6 h-16">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{getRoleDisplay(user?.role)}</span>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground font-medium">
              {flatNavItems.find(item => location.pathname.startsWith(item.href))?.label || 'Dashboard'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon">
              <Bell className="h-5 w-5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="btn-user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-amber-100 text-amber-700">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email || user?.phone}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="btn-logout">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 md:p-6 pt-16 md:pt-6 pb-20 md:pb-6">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav safe-area-bottom">
        <div className="flex justify-around items-center h-16">
          {flatNavItems.slice(0, 5).map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) =>
                cn(
                  'mobile-nav-item',
                  isActive && 'active text-amber-600'
                )
              }
            >
              <item.icon className="h-5 w-5" />
              <span className="text-xs mt-1">{item.label.split(' ')[0]}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
